import { Flex, FormControl, FormLabel, Switch } from '@chakra-ui/react';
import { MAP_EDITED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getMapTypeForEvent } from 'libs/third-party/Mixpanel/mixpanel.helper';
import {
  ChartSettingsDivider,
  DataPrefix,
  DataSuffix,
  SettingsAccordion,
} from 'modules/Editor/components/SideMenuSettings/ChartSettings';
import { ChangeEvent, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { getOutlineColorBasedOnLuminance } from 'utils/color';
import { FontSettings, TextSettings } from 'widgets/ChartWidgets/DataTableChartCommon/TextStyleSettings';
import { useWidget } from 'widgets/sdk';

import { MapWidgetData, ScaleType } from '../MapWidget.types';

export const LabelSettings = () => {
  const {
    series: {
      dataLabels: { showValue, format, enabled },
    },
  } = useWidget<MapWidgetData>();
  const safeShowValue = typeof showValue === 'boolean' ? showValue : format === '{point.value}' && enabled;

  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });

  return (
    <SettingsAccordion isDefaultOpen title={t('mapLabelsTitle')}>
      <LabelFormatNameToggle />
      <LabelFormatValueToggle />
      {safeShowValue && <DataLabelAdornment />}
      <ChartSettingsDivider />
      <FontStyle />
      <TextStyle />
      <TextOutlineSwitcher />
    </SettingsAccordion>
  );
};

const LabelFormatNameToggle = () => {
  const {
    mapType,
    scaleType = ScaleType.Steps,
    series: { dataLabels },
    updateWidget,
  } = useWidget<MapWidgetData>();
  const { showName, format, enabled } = dataLabels;
  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });
  const label = t('showNameLabel');
  // Example id: 'show-name-toggle:rjp:'
  const switchId = label.replaceAll(' ', '-').toLowerCase() + '-toggle' + useId();
  const safeShowName = typeof showName === 'boolean' ? showName : format === '{point.name}' && enabled;

  const onShowNameToggle = () => {
    updateWidget({
      series: {
        dataLabels: {
          showName: !safeShowName,
        },
      },
    });
    Mixpanel.track(MAP_EDITED.eventName, {
      styling_property: MAP_EDITED.stylingProperty.Labels,
      styling_value: !safeShowName ? MAP_EDITED.stylingValue.NameLabelsOn : MAP_EDITED.stylingValue.NameLabelsOff,
      map_type: getMapTypeForEvent(MAP_EDITED, mapType, scaleType),
    });
  };

  return (
    <Flex alignItems='center' gap={3} justifyContent='space-between' color='font.500' fontSize='sm' mt={1} mb={2}>
      <FormControl display='flex' alignItems='center'>
        <FormLabel htmlFor={switchId} mr='auto' fontSize='sm' fontWeight='400' mb='0'>
          {label}
        </FormLabel>
        <Switch onChange={onShowNameToggle} isChecked={safeShowName} id={switchId} />
      </FormControl>
    </Flex>
  );
};

const LabelFormatValueToggle = () => {
  const {
    mapType,
    scaleType = ScaleType.Steps,
    series: { dataLabels },
    updateWidget,
  } = useWidget<MapWidgetData>();
  const { showValue, format, enabled } = dataLabels;
  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });
  const label = t('showValueLabel');
  const switchId = label.replaceAll(' ', '-').toLowerCase() + '-toggle' + useId();
  const safeShowValue = typeof showValue === 'boolean' ? showValue : format === '{point.value}' && enabled;

  const onShowValueToggle = () => {
    updateWidget({
      series: {
        dataLabels: {
          showValue: !safeShowValue,
        },
      },
    });
    Mixpanel.track(MAP_EDITED.eventName, {
      styling_property: MAP_EDITED.stylingProperty.Labels,
      styling_value: !safeShowValue ? MAP_EDITED.stylingValue.ValueLabelsOn : MAP_EDITED.stylingValue.ValueLabelsOff,
      map_type: getMapTypeForEvent(MAP_EDITED, mapType, scaleType),
    });
  };

  return (
    <Flex alignItems='center' gap={3} justifyContent='space-between' color='font.500' fontSize='sm' my={2}>
      <FormControl display='flex' alignItems='center'>
        <FormLabel htmlFor={switchId} mr='auto' fontSize='sm' fontWeight='400' mb='0'>
          {label}
        </FormLabel>
        <Switch onChange={onShowValueToggle} isChecked={safeShowValue} id={switchId} />
      </FormControl>
    </Flex>
  );
};

const DataLabelAdornment = () => {
  const {
    updateWidget,
    series: {
      dataLabels: { prefix = '', suffix = '' },
    },
  } = useWidget<MapWidgetData>();

  const onPrefixChange = (event: ChangeEvent<HTMLInputElement>) => {
    const prefixValue = event.target.value;
    updateWidget({ series: { dataLabels: { prefix: prefixValue } } });
  };

  const onSuffixChange = (event: ChangeEvent<HTMLInputElement>) => {
    const suffixValue = event.target.value;
    updateWidget({ series: { dataLabels: { suffix: suffixValue } } });
  };

  return (
    <>
      <DataPrefix value={prefix} onChange={onPrefixChange} />
      <DataSuffix value={suffix} onChange={onSuffixChange} />
    </>
  );
};

const FontStyle = () => {
  const {
    series: {
      dataLabels: {
        style: { fontFamily, fontSize },
      },
    },
    updateWidget,
  } = useWidget<MapWidgetData>();

  const onFontFamilyChange = (fontFamily: string) => {
    updateWidget({
      series: {
        dataLabels: {
          style: {
            fontFamily: fontFamily,
          },
        },
      },
    });
  };

  const onFontSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const fontSize = event.target.value;
    updateWidget({
      series: {
        dataLabels: {
          style: {
            fontSize: `${fontSize}px`,
          },
        },
      },
    });
  };

  return (
    <FontSettings
      fontFamily={fontFamily ? fontFamily : ''}
      fontSize={fontSize ? fontSize : ''}
      onChangeFontFamily={onFontFamilyChange}
      onChangeFontSize={onFontSizeChange}
    />
  );
};

const TextStyle = () => {
  const {
    series: {
      dataLabels: {
        style: { color, fontWeight, fontStyle, fontFamily },
      },
    },
    updateWidget,
  } = useWidget<MapWidgetData>();

  // Color onChanges happen too often to be traced in Mixpanel
  const onFontColorChange = (fontColor: string) => {
    updateWidget({
      series: {
        dataLabels: {
          style: {
            color: fontColor,
          },
        },
      },
    });
  };

  const onFontWeightChange = (fontWeight: string) => {
    updateWidget({
      series: {
        dataLabels: {
          style: {
            fontWeight: fontWeight,
          },
        },
      },
    });
  };

  const onFontStyleChange = (fontStyle: string) => {
    updateWidget({
      series: {
        dataLabels: {
          style: {
            fontStyle: fontStyle,
          },
        },
      },
    });
  };

  return (
    <TextSettings
      fontFamily={fontFamily ? fontFamily : ''}
      fontColor={color ? color : ''}
      fontWeight={fontWeight ? fontWeight : ''}
      fontStyle={fontStyle ? fontStyle : ''}
      onFontColorChange={onFontColorChange}
      onFontWeightChange={onFontWeightChange}
      onFontStyleChange={onFontStyleChange}
    />
  );
};

const TextOutlineSwitcher = () => {
  const {
    series: {
      dataLabels: {
        style: { textOutline, color },
      },
    },
    updateWidget,
  } = useWidget<MapWidgetData>();
  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });
  const label = t('textOutlineLabel');
  const switchId = label.replaceAll(' ', '-').toLowerCase() + '-toggle' + useId();

  const isChecked = textOutline === 'none' ? false : true;

  const onTextOutlineChange = (textOutline: boolean | string) => {
    let actualTextOutline;
    if (textOutline === false) {
      actualTextOutline = 'none';
    } else if (textOutline === true && color) {
      actualTextOutline = `1px ${getOutlineColorBasedOnLuminance(color)}`;
    }
    updateWidget({
      series: {
        dataLabels: {
          style: {
            textOutline: actualTextOutline,
          },
        },
      },
    });
  };

  return (
    <Flex alignItems='center' gap={3} justifyContent='space-between' color='font.500' fontSize='sm' py='2' mt='2'>
      <FormControl display='flex' alignItems='center'>
        <FormLabel htmlFor={switchId} mr='auto' fontSize='sm' fontWeight='400' mb='0'>
          {label}
        </FormLabel>
        <Switch
          onChange={() => {
            onTextOutlineChange(!isChecked);
          }}
          isChecked={isChecked}
          id={switchId}
        />
      </FormControl>
    </Flex>
  );
};
